import React, { useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  LinearProgress,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import BotNoteView from "components/botsManagement/BotNoteView";
import {
  BotNote,
  BotNoteLink,
  BotRegistryItem,
  useGetBotNotesQuery,
  useGetBotRegistryQuery,
  useSaveBotNoteMutation,
} from "features/adminUsers/adminUsers";

type Scope = "vendor" | "bot";

/**
 * The one place the notes are written (CAL-6162).
 *
 * The catalogue is code, so this lists what the registry answers with and
 * lets an operator write beside it - never add to it. A note whose key the
 * registry has stopped answering for is listed at the end: it is kept rather
 * than deleted, because a bot renamed by mistake would take its note along.
 */
export default function BotNotesPanel({
  onBack,
  canEdit,
}: {
  onBack: () => void;
  canEdit: boolean;
}) {
  // The whole catalogue, disabled bots included: a note may be written for
  // one, and hiding it here would leave no way to reach it.
  const registry = useGetBotRegistryQuery({ isActive: null, pageSize: 500 });
  const notes = useGetBotNotesQuery();
  const [save, saving] = useSaveBotNoteMutation();
  const [editing, setEditing] = useState<{ scope: Scope; key: string } | null>(null);
  const [draft, setDraft] = useState("");
  const [draftLinks, setDraftLinks] = useState<BotNoteLink[]>([]);
  const [error, setError] = useState<string | null>(null);

  const payload =
    registry.data && !Array.isArray(registry.data) ? registry.data : undefined;
  const rows: BotRegistryItem[] = useMemo(() => {
    const data = registry.data;
    return Array.isArray(data) ? data : (data?.results ?? []);
  }, [registry.data]);

  const byVendor: Array<[string, BotRegistryItem[]]> = useMemo(() => {
    const out: Record<string, BotRegistryItem[]> = {};
    for (const row of rows) {
      (out[row.vendor] = out[row.vendor] ?? []).push(row);
    }
    return Object.keys(out)
      .sort((a, b) => a.localeCompare(b))
      .map((vendor) => [vendor, out[vendor]]);
  }, [rows]);

  const open = (scope: Scope, key: string, note: BotNote | null | undefined) => {
    setEditing({ scope, key });
    setDraft(note?.text ?? "");
    setDraftLinks((note?.links ?? []).map((link) => ({ ...link })));
    setError(null);
  };

  const commit = async () => {
    if (!editing) return;
    setError(null);
    try {
      await save({
        scope: editing.scope,
        key: editing.key,
        text: draft,
        links: draftLinks.filter((link) => link.label.trim() || link.url.trim()),
      }).unwrap();
      await notes.refetch();
      await registry.refetch();
      setEditing(null);
    } catch (e) {
      const said = (e as { data?: { message?: string } })?.data?.message;
      setError(said || "The note was not saved.");
    }
  };

  const editor = (scope: Scope, key: string, note: BotNote | null | undefined) => {
    const isOpen = editing?.scope === scope && editing?.key === key;
    if (!isOpen) {
      return (
        <Button size="small" disabled={!canEdit} onClick={() => open(scope, key, note)}>
          {note?.text ? "Edit note" : "Add note"}
        </Button>
      );
    }
    return (
      <Stack spacing={0.5} sx={{ width: "100%" }}>
        <TextField
          multiline
          minRows={2}
          size="small"
          fullWidth
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="What somebody running this needs to know. No credentials, no client data - link the Confluence page instead."
        />
        {draftLinks.map((link, index) => (
          <Stack direction="row" spacing={0.5} key={index}>
            <TextField
              size="small"
              label="Page title"
              value={link.label}
              onChange={(e) =>
                setDraftLinks((rows) =>
                  rows.map((row, at) =>
                    at === index ? { ...row, label: e.target.value } : row,
                  ),
                )
              }
            />
            <TextField
              size="small"
              fullWidth
              label="https://..."
              value={link.url}
              onChange={(e) =>
                setDraftLinks((rows) =>
                  rows.map((row, at) =>
                    at === index ? { ...row, url: e.target.value } : row,
                  ),
                )
              }
            />
            <Button
              size="small"
              onClick={() =>
                setDraftLinks((rows) => rows.filter((_, at) => at !== index))
              }
            >
              Remove
            </Button>
          </Stack>
        ))}
        <Stack direction="row" spacing={1}>
          <Button
            size="small"
            onClick={() => setDraftLinks((rows) => [...rows, { label: "", url: "" }])}
          >
            Add link
          </Button>
        </Stack>
        <Stack direction="row" spacing={1}>
          <Button size="small" variant="contained" disabled={saving.isLoading} onClick={() => void commit()}>
            Save
          </Button>
          <Button size="small" onClick={() => setEditing(null)}>
            Cancel
          </Button>
          <Typography variant="caption" sx={{ color: "text.secondary", alignSelf: "center" }}>
            An empty note removes it.
          </Typography>
        </Stack>
      </Stack>
    );
  };

  const loading = registry.isLoading || notes.isLoading;

  return (
    <Box>
      <Stack direction="row" alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="h6" fontWeight={700}>
          Notes for DPM
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Button onClick={onBack}>Back</Button>
      </Stack>
      <Typography variant="body2" sx={{ color: "text.secondary", mb: 1 }}>
        What a DPM needs before running a bot: how many instances the vendor
        tolerates, what to check first, where its page is. The bots themselves
        come from the code - a note cannot add one or hide one.
      </Typography>
      {loading ? <LinearProgress sx={{ mb: 1 }} /> : null}
      {error ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          {error}
        </Alert>
      ) : null}

      {byVendor.map(([vendor, bots]) => {
        const vendorNote = payload?.vendors?.[vendor]?.note ?? null;
        return (
          <Box key={vendor} sx={{ mb: 2, border: 1, borderColor: "divider", borderRadius: 1, p: 1.25 }}>
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
              <Typography fontWeight={700}>{vendor}</Typography>
              <Chip size="small" variant="outlined" label={`${bots.length} bot(s)`} sx={{ height: 20 }} />
              <Box sx={{ flex: 1 }} />
              {editing?.scope === "vendor" && editing.key === vendor
                ? null
                : editor("vendor", vendor, vendorNote)}
            </Stack>
            {editing?.scope === "vendor" && editing.key === vendor
              ? editor("vendor", vendor, vendorNote)
              : <BotNoteView note={vendorNote} dense />}

            <Divider sx={{ my: 1 }} />
            {bots.map((bot: BotRegistryItem) => (
              <Box key={bot.bot_id} sx={{ py: 0.5 }}>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                    {`${bot.job_kind}.${bot.bot_slug}`}
                  </Typography>
                  <Box sx={{ flex: 1 }} />
                  {editing?.scope === "bot" && editing.key === bot.bot_id
                    ? null
                    : editor("bot", bot.bot_id, bot.note)}
                </Stack>
                {editing?.scope === "bot" && editing.key === bot.bot_id
                  ? editor("bot", bot.bot_id, bot.note)
                  : <BotNoteView note={bot.note} dense />}
              </Box>
            ))}
          </Box>
        );
      })}

      {notes.data?.orphans?.length ? (
        <Box sx={{ mt: 2 }}>
          <Typography variant="subtitle2" fontWeight={700}>
            Notes whose bot or vendor this build does not know
          </Typography>
          <Typography variant="caption" sx={{ color: "text.secondary" }}>
            Kept, and shown nowhere else. Repoint them, or clear them.
          </Typography>
          {notes.data.orphans.map((row) => (
            <Box key={`${row.scope}:${row.key}`} sx={{ py: 0.5 }}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Chip size="small" label={row.scope} sx={{ height: 20 }} />
                <Typography variant="body2" sx={{ fontFamily: "monospace" }}>
                  {row.key}
                </Typography>
                <Box sx={{ flex: 1 }} />
                {editor(row.scope as Scope, row.key, row.note)}
              </Stack>
              <BotNoteView note={row.note} dense />
            </Box>
          ))}
        </Box>
      ) : null}

      {!loading && !byVendor.length ? (
        <Alert severity={registry.isError ? "error" : "info"} sx={{ mt: 2 }}>
          {registry.isError ? (
            <Stack direction="row" spacing={1} alignItems="center">
              <span>The bot catalogue could not be loaded.</span>
              <Button size="small" onClick={() => void registry.refetch()}>
                Try again
              </Button>
            </Stack>
          ) : (
            "This build registers no bots, so there is nothing to note."
          )}
        </Alert>
      ) : null}
    </Box>
  );
}
